import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  Col,
  FilterAccordionHeader,
  KeyValue,
  Row,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { ViewMetaData } from '@folio/stripes/smart-components';
import { getLocalizedValue } from '../../../../../helpers/localizationUtils';

function MainMetaData({ selectedReport, categories = [], reportTypes = [] }) {
  const [filterToggle, setFilterToggle] = useState(true);
  const intl = useIntl();
  const { locale } = intl;

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return categoryId || '-';
    return intl.formatMessage({ id: `ui-reports.categories.name.${cat.name}`, defaultMessage: cat.name });
  };

  const getReportTypeName = (reportTypeId) => {
    const rt = reportTypes.find(r => r.id === reportTypeId);
    if (!rt) return reportTypeId || '-';
    return intl.formatMessage({ id: `ui-reports.reportTypes.name.${rt.code}`, defaultMessage: rt.name });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(locale);
  };

  return (
    <Accordion
      id="report-details-main-meta-data-view-accordion"
      label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.accordion.header" defaultMessage="Main data" />}
      onToggle={() => setFilterToggle(!filterToggle)}
      open={filterToggle}
      separator={false}
      header={FilterAccordionHeader}
    >
      <Row>
        <Col xs={12}>
          <ViewMetaData metadata={selectedReport?.metadata} />
        </Col>
      </Row>

      {/* Name & Description */}
      <Row>
        <Col xs={6}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.name" defaultMessage="Report name" />}
            value={getLocalizedValue(selectedReport?.name, locale)}
          />
        </Col>
        <Col xs={6}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.desc" defaultMessage="Description" />}
            value={getLocalizedValue(selectedReport?.desc, locale)}
          />
        </Col>
      </Row>

      {/* Status & Visibility */}
      <Row>
        <Col xs={4}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.status" defaultMessage="Status" />}
            value={
              <FormattedMessage
                id={`ui-reports.filters.status.${selectedReport?.status}`}
                defaultMessage={selectedReport?.status}
              />
            }
          />
        </Col>
        <Col xs={4}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.privacyType" defaultMessage="Visibility" />}
            value={(() => {
              const vis = selectedReport?.permissions?.visibility || 'public';
              const visibilityColors = {
                public:     { bg: '#e8f5e9', color: '#1b5e20', border: '#4caf50' },
                private:    { bg: '#fff3e0', color: '#e65100', border: '#ff9800' },
                restricted: { bg: '#e3f2fd', color: '#0d47a1', border: '#2196f3' },
              };
              const s = visibilityColors[vis] || visibilityColors.public;
              return (
                <span style={{
                  display: 'inline-block',
                  padding: '0.15em 0.5em',
                  borderRadius: '0.25rem',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  backgroundColor: s.bg,
                  color: s.color,
                  border: `1px solid ${s.border}`,
                }}>
                  <FormattedMessage
                    id={`ui-reports.filters.privacyType.${vis}`}
                    defaultMessage={vis}
                  />
                </span>
              );
            })()}
          />
        </Col>
        <Col xs={4}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.builderType" defaultMessage="Builder type" />}
            value={
              <FormattedMessage
                id={`ui-reports.filters.builderType.${selectedReport?.builderType}`}
                defaultMessage={selectedReport?.builderType}
              />
            }
          />
        </Col>
      </Row>

      {/* Category & Report Type */}
      <Row>
        <Col xs={6}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.filters.category" defaultMessage="Category" />}
            value={getCategoryName(selectedReport?.categoryId)}
          />
        </Col>
        <Col xs={6}>
          <KeyValue
            label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.reportType" defaultMessage="Report type" />}
            value={getReportTypeName(selectedReport?.reportType)}
          />
        </Col>
      </Row>

      {/* Published / Archived dates */}
      {selectedReport?.publishedAt && (
        <Row>
          <Col xs={6}>
            <KeyValue
              label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.publishedAt" defaultMessage="Published at" />}
              value={formatDate(selectedReport.publishedAt)}
            />
          </Col>
          {selectedReport?.archivedAt && (
            <Col xs={6}>
              <KeyValue
                label={<FormattedMessage id="ui-reports.reportDetails.mainMetaData.archivedAt" defaultMessage="Archived at" />}
                value={formatDate(selectedReport.archivedAt)}
              />
            </Col>
          )}
        </Row>
      )}

      {/* Performance */}
      {selectedReport?.performance && (
        <Row>
          <Col xs={4}>
            <KeyValue
              label={<FormattedMessage id="ui-reports.performance.complexity" defaultMessage="Complexity" />}
              value={
                selectedReport.performance.complexity
                  ? <FormattedMessage
                      id={`ui-reports.performance.complexity.${selectedReport.performance.complexity}`}
                      defaultMessage={selectedReport.performance.complexity}
                    />
                  : '-'
              }
            />
          </Col>
          <Col xs={4}>
            <KeyValue
              label={<FormattedMessage id="ui-reports.performance.dataVolume" defaultMessage="Data Volume" />}
              value={
                selectedReport.performance.dataVolume
                  ? <FormattedMessage
                      id={`ui-reports.performance.dataVolume.${selectedReport.performance.dataVolume}`}
                      defaultMessage={selectedReport.performance.dataVolume}
                    />
                  : '-'
              }
            />
          </Col>
          <Col xs={4}>
            <KeyValue
              label={<FormattedMessage id="ui-reports.performance.maxExecutionTime" defaultMessage="Max Execution Time (ms)" />}
              value={selectedReport.performance.maxExecutionTime || '-'}
            />
          </Col>
        </Row>
      )}
    </Accordion>
  );
}

MainMetaData.propTypes = {
  selectedReport: PropTypes.object,
  categories: PropTypes.arrayOf(PropTypes.object),
  reportTypes: PropTypes.arrayOf(PropTypes.object),
};

export default MainMetaData;
